import React, { useMemo, useState } from 'react';
import { Filters } from './apiClient';
import MultiCombobox, { ComboOption } from './MultiCombobox';
import { theme } from './theme';
import { WarehouseRow } from './useWarehouseRows';
import { useAliases, AliasCategory, AllAliases } from './useAliases';
import ChamberRenameModal, { parseChamberAlias } from './ChamberRenameModal';

/* ============================================================================
 * FilterBar — three multi-select filters for the Operations dashboard.
 *
 *   1) WAREHOUSE      — list of warehouse codes from the loaded rows
 *   2) CH (= AREA)    — physical chamber / storage area (STORAGE_AREA)
 *   3) PALLET TYPES   — SHELF_SIZE codes
 *
 *  Empty selection in any filter = "All" for that dimension.
 *  All filters are AND-combined when computing KPIs.
 *
 *  Aliases are stored in the BACKEND (Azure SQL → DisplayAliases table) so
 *  every user sees the same friendly names.
 * ========================================================================== */

type Props = {
  rows: WarehouseRow[];
  customerStorage?: any[];   // includes MANUAL-only warehouses (e.g. RUH_M)
  value: Filters;
  onChange: (f: Filters) => void;
};

const FilterBar: React.FC<Props> = ({ rows, customerStorage, value, onChange }) => {
  const { aliases, save, resolve, resolveWithCode } = useAliases();
  const [renameOpen, setRenameOpen] = useState<AliasCategory | null>(null);

  // ── Option builders (scoped to current filter state) ──────────────

  const warehouseOptions: ComboOption[] = useMemo(() => {
    const EXCLUDED = new Set(['LFS']);
    const set = new Set<string>();
    rows.forEach(r => { if (!EXCLUDED.has(r.WAREHOUSE)) set.add(r.WAREHOUSE); });
    // Include manual-only warehouses from customer storage (e.g. RUH_M)
    (customerStorage || []).forEach((r: any) => {
      const wh = String(r.WAREHOUSE || r.warehouse || '').trim();
      if (wh && !EXCLUDED.has(wh)) set.add(wh);
    });
    return Array.from(set).sort().map(c => ({
      value: c,
      label: resolve('warehouse', c),
    }));
  }, [rows, customerStorage, aliases, resolve]);

  // Chamber value = composite "WAREHOUSE-STORAGE_AREA" so it's globally unique.
  // This ensures filtering by area never leaks data from another warehouse.
  const chamberOptions: ComboOption[] = useMemo(() => {
    const EXCLUDED = new Set(['LFS']);
    const filtered = value.warehouses.length
      ? rows.filter(r => value.warehouses.includes(r.WAREHOUSE))
      : rows;
    const set = new Map<string, string>();
    filtered.forEach(r => {
      if (!r.STORAGE_AREA || EXCLUDED.has(r.WAREHOUSE)) return;
      const compositeKey = `${r.WAREHOUSE}-${r.STORAGE_AREA}`;
      if (set.has(compositeKey)) return;
      // rich chamber options: code is the label, alias text is the description
      // value stored under aliases.area[compositeKey] as JSON {icon, desc}
      const raw = aliases.area?.[compositeKey] || aliases.area?.[r.STORAGE_AREA] || '';
      const parsed = parseChamberAlias(raw);
      const lbl = value.warehouses.length === 1
        ? r.STORAGE_AREA
        : `${resolve('warehouse', r.WAREHOUSE)} · ${r.STORAGE_AREA}`;
      set.set(compositeKey, JSON.stringify({ lbl, icon: parsed.icon, desc: parsed.desc }));
    });
    // chamber options return rich shape
    return Array.from(set.entries())
      .map(([value, packed]) => {
        const { lbl, icon, desc } = JSON.parse(packed);
        return { value, label: lbl, icon, description: desc };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [rows, value.warehouses, aliases, resolve]);

  const palletTypeOptions: ComboOption[] = useMemo(() => {
    let scope = rows;
    if (value.warehouses.length) scope = scope.filter(r => value.warehouses.includes(r.WAREHOUSE));
    if (value.chambers.length)   scope = scope.filter(r => value.chambers.includes(`${r.WAREHOUSE}-${r.STORAGE_AREA}`));
    const set = new Set<string>();
    scope.forEach(r => { if (r.SHELF_SIZE) set.add(r.SHELF_SIZE); });
    return Array.from(set).sort().map(v => ({
      value: v,
      label: resolveWithCode('palletType', v),
    }));
  }, [rows, value.warehouses, value.chambers, aliases, resolveWithCode]);

  // ── Full code lists (unscoped) for the rename modal ───────────────

  const allWarehouseCodes = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => set.add(r.WAREHOUSE));
    return Array.from(set).sort();
  }, [rows]);

  const allAreaCodes = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.STORAGE_AREA) set.add(r.STORAGE_AREA); });
    return Array.from(set).sort();
  }, [rows]);

  const allPalletCodes = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => { if (r.SHELF_SIZE) set.add(r.SHELF_SIZE); });
    return Array.from(set).sort();
  }, [rows]);

  // Composite chamber codes, scoped per warehouse, e.g. "Modon 3A-CHM-04".
  // Stored in the existing "area" alias bucket — composite keys cannot
  // collide with bare area codes because bare codes never contain a
  // warehouse prefix.
  const allChamberCodes = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(r => {
      if (r.STORAGE_AREA && r.WAREHOUSE) {
        set.add(`${r.WAREHOUSE}-${r.STORAGE_AREA}`);
      }
    });
    return Array.from(set).sort();
  }, [rows]);

  const allCustomerCodes = useMemo(() => {
    const set = new Set<string>();
    (customerStorage || []).forEach((r: any) => {
      const c = String(r.CUSTOMER || r.customer || '').trim();
      if (c) set.add(c);
    });
    return Array.from(set).sort();
  }, [customerStorage]);

  const codesFor = (cat: AliasCategory) =>
    cat === 'warehouse' ? allWarehouseCodes
    : cat === 'area' ? allChamberCodes   // per-warehouse composite chamber codes
    : cat === 'customer' ? allCustomerCodes
    : allPalletCodes;

  const labelFor = (cat: AliasCategory) =>
    cat === 'warehouse' ? 'Warehouse'
    : cat === 'area' ? 'Chamber (per warehouse)'
    : cat === 'customer' ? 'Customer'
    : 'Pallet Type';

  // ── Render ────────────────────────────────────────────────────────

  const renameBtn = (cat: AliasCategory) => (
    <button
      onClick={() => setRenameOpen(cat)}
      title={`Rename ${labelFor(cat)} codes`}
      style={{
        height: 38, width: 38, borderRadius: 8,
        border: '1px solid #e5eaf2', background: '#fff',
        color: theme.textSecondary, fontWeight: 700, fontSize: 14,
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
      ✏︎
    </button>
  );

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 18,
      justifyContent: 'center',
      padding: 0, background: 'transparent', border: 'none', boxShadow: 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        <MultiCombobox
          label="Warehouse"
          values={value.warehouses}
          options={warehouseOptions}
          placeholder="All Warehouses"
          onChange={(vals) => onChange({ ...value, warehouses: vals, chambers: [] })}
          width={240}
        />
        {renameBtn('warehouse')}
      </div>

      <div style={{ width: 1, alignSelf: 'stretch', background: '#eef2f8', margin: '4px 0' }} />

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        <MultiCombobox
          label="Chamber / Area"
          values={value.chambers}
          options={chamberOptions}
          placeholder="All Chambers"
          onChange={(vals) => onChange({ ...value, chambers: vals })}
          width={260}
        />
        {renameBtn('area')}
      </div>

      <div style={{ width: 1, alignSelf: 'stretch', background: '#eef2f8', margin: '4px 0' }} />

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        <MultiCombobox
          label="Pallet Type"
          values={value.palletTypes}
          options={palletTypeOptions}
          placeholder="All Pallet Types"
          onChange={(vals) => onChange({ ...value, palletTypes: vals })}
          width={220}
        />
        {renameBtn('palletType')}
      </div>

      <div style={{ width: 1, alignSelf: 'stretch', background: '#eef2f8', margin: '4px 0' }} />

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, alignSelf: 'flex-end', paddingBottom: 2 }}>
        <button
          onClick={() => setRenameOpen('customer')}
          title="Rename Customer codes"
          style={{
            height: 38, padding: '0 14px', borderRadius: 8,
            border: '1px solid #e5eaf2', background: '#fff',
            color: theme.textSecondary, fontWeight: 600, fontSize: 12,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            fontFamily: 'inherit',
          }}>
          <span style={{ fontSize: 14 }}>👤</span> Rename Customers
        </button>
      </div>

      {/* Row count + clear */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        fontSize: 12, color: theme.textMuted, fontWeight: 500,
        alignSelf: 'flex-end', paddingBottom: 6,
      }}>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{rows.length.toLocaleString()} rows</span>
        {(value.warehouses.length + value.chambers.length + value.palletTypes.length > 0) && (
          <button
            onClick={() => onChange({ warehouses: [], chambers: [], palletTypes: [] })}
            style={{
              padding: '7px 14px', borderRadius: 8,
              border: '1px solid #e5eaf2', background: '#fff',
              color: theme.textSecondary, fontWeight: 600, fontSize: 12, cursor: 'pointer',
              fontFamily: 'inherit',
            }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Rename modal — chamber rename uses the rich icon+description editor */}
      {renameOpen === 'area' ? (
        <ChamberRenameModal
          codes={codesFor('area')}
          currentAliases={aliases.area || {}}
          onSave={async (next) => {
            // Merge with existing area aliases so legacy bare-code entries survive
            const merged: Record<string, string> = { ...(aliases.area || {}) };
            // Strip out any composite-key entries that are no longer set
            codesFor('area').forEach(code => { delete merged[code]; });
            // Then layer the new chamber entries on top
            Object.entries(next).forEach(([k, v]) => { merged[k] = v; });
            const updated: AllAliases = { ...aliases, area: merged };
            await save(updated);
            setRenameOpen(null);
          }}
          onClose={() => setRenameOpen(null)}
        />
      ) : renameOpen ? (
        <AliasModal
          category={renameOpen}
          categoryLabel={labelFor(renameOpen)}
          codes={codesFor(renameOpen)}
          currentAliases={aliases[renameOpen] || {}}
          onSave={async (next) => {
            const updated: AllAliases = { ...aliases, [renameOpen]: next };
            await save(updated);
            setRenameOpen(null);
          }}
          onClose={() => setRenameOpen(null)}
        />
      ) : null}
    </div>
  );
};

/* ============================================================================
 * AliasModal — reusable modal for renaming codes in any category.
 * ========================================================================== */
const AliasModal: React.FC<{
  category: AliasCategory;
  categoryLabel: string;
  codes: string[];
  currentAliases: Record<string, string>;
  onSave: (m: Record<string, string>) => void;
  onClose: () => void;
}> = ({ category, categoryLabel, codes, currentAliases, onSave, onClose }) => {
  const [draft, setDraft] = useState<Record<string, string>>(currentAliases);
  const [saving, setSaving] = useState(false);
  const set = (code: string, label: string) => setDraft(d => ({ ...d, [code]: label }));
  const clearOne = (code: string) => setDraft(d => {
    const n = { ...d }; delete n[code]; return n;
  });

  const handleSave = async () => {
    setSaving(true);
    const clean: Record<string, string> = {};
    Object.entries(draft).forEach(([k, v]) => { const t = String(v).trim(); if (t) clean[k] = t; });
    await onSave(clean);
    setSaving(false);
  };

  const placeholders: Record<AliasCategory, string> = {
    warehouse: 'e.g. Main Warehouse',
    area: 'e.g. Temperature Controlled',
    palletType: 'e.g. Big Pallet',
    customer: 'e.g. Acme Corporation',
  };

  const iconFor: Record<AliasCategory, string> = {
    warehouse: '🏭',
    area: '📍',
    palletType: '📦',
    customer: '👤',
  };

  return (
    <>
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0, background: 'rgba(15,25,55,0.35)', zIndex: 1200,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: 520, maxWidth: '95vw', maxHeight: '85vh', background: '#fff',
        borderRadius: 16, boxShadow: '0 30px 80px rgba(15,25,55,0.35)',
        zIndex: 1201, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #eef2f8',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #f8faff 0%, #eef4ff 100%)',
          borderRadius: '16px 16px 0 0',
        }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 10, fontWeight: 700, color: '#6366f1', textTransform: 'uppercase',
              letterSpacing: 1, padding: '3px 10px', borderRadius: 999,
              background: '#fff', border: '1px solid #e0e7ff', marginBottom: 6,
            }}>
              {iconFor[category]} {categoryLabel} Aliases
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: theme.textPrimary }}>
              Rename {categoryLabel} Codes
            </div>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 2 }}>
              Names are saved to the server — all users will see these labels.
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: '#eef2f8', width: 32, height: 32, borderRadius: 8,
            cursor: 'pointer', fontSize: 16, fontWeight: 700, color: theme.textSecondary,
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: '14px 24px', overflowY: 'auto', flex: 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f6fb', fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                <th style={{ padding: '10px 12px', textAlign: 'left', width: 120, borderRadius: '8px 0 0 8px' }}>Code</th>
                <th style={{ padding: '10px 12px', textAlign: 'left' }}>Display Name</th>
                <th style={{ padding: '10px 12px', width: 36, borderRadius: '0 8px 8px 0' }}></th>
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 && (
                <tr><td colSpan={3} style={{ padding: 20, textAlign: 'center', color: theme.textMuted, fontSize: 13 }}>No codes loaded yet.</td></tr>
              )}
              {codes.map(code => (
                <tr key={code} style={{ borderTop: '1px solid #f3f6fb' }}>
                  <td style={{
                    padding: '10px 12px', fontWeight: 800,
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    color: theme.textPrimary, fontSize: 13,
                  }}>
                    <span style={{
                      background: '#eef2ff', padding: '3px 8px', borderRadius: 6,
                      border: '1px solid #e0e7ff', color: '#4338ca',
                    }}>{code}</span>
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    <input
                      value={draft[code] || ''}
                      onChange={(e) => set(code, e.target.value)}
                      placeholder={placeholders[category]}
                      style={{
                        width: '100%', padding: '8px 12px', fontSize: 13,
                        border: '1px solid #d8dee9', borderRadius: 8,
                        outline: 'none', fontFamily: 'inherit', color: theme.textPrimary,
                        transition: 'border-color 0.15s',
                      }}
                      onFocus={(e) => { e.target.style.borderColor = '#6366f1'; }}
                      onBlur={(e) => { e.target.style.borderColor = '#d8dee9'; }}
                    />
                  </td>
                  <td style={{ padding: '8px 6px', textAlign: 'center' }}>
                    {draft[code] && (
                      <button onClick={() => clearOne(code)} title="Clear" style={{
                        border: 'none', background: '#fef2f2', color: '#dc2626',
                        cursor: 'pointer', fontSize: 13, width: 26, height: 26,
                        borderRadius: 6, fontWeight: 700,
                      }}>×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #eef2f8',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: '#fafbfd', borderRadius: '0 0 16px 16px',
        }}>
          <button onClick={() => setDraft({})} style={{
            padding: '9px 16px', borderRadius: 8, border: '1px solid #fecaca',
            background: '#fff', color: '#dc2626', fontWeight: 600, fontSize: 12,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>Clear all</button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '9px 16px', borderRadius: 8, border: '1px solid #e5eaf2',
              background: '#fff', color: theme.textSecondary, fontWeight: 600, fontSize: 12,
              cursor: 'pointer', fontFamily: 'inherit',
            }}>Cancel</button>
            <button onClick={handleSave} disabled={saving} style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              background: saving ? '#94a3b8' : 'linear-gradient(135deg, #4f46e5, #6366f1)',
              color: '#fff', fontWeight: 700, fontSize: 12, cursor: saving ? 'default' : 'pointer',
              fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
            }}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default FilterBar;
